import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, Mail, Plus, Trash2, Loader2, Send, AlertTriangle, Clock, ShieldCheck, Coffee } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { AppLinksCard } from "@/components/AppLinksCard";

const THRESHOLDS = [5, 10, 20, 30];

export default function NotificationSettings() {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const { data: settings, isLoading } = useQuery({
    queryKey: ["notif-settings"],
    queryFn: () => api.get("/admin/notifications/settings"),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["notif-settings"] });

  const updateMut = useMutation({
    mutationFn: (body) => api.put("/admin/notifications/settings", body),
    onSuccess: () => { invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const addMut = useMutation({
    mutationFn: (body) => api.post("/admin/notifications/recipients", body),
    onSuccess: () => { toast.success("Recipient added"); setEmail(""); setName(""); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/admin/notifications/recipients/${id}`),
    onSuccess: () => { toast.success("Recipient removed"); invalidate(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const testMut = useMutation({
    mutationFn: () => api.post("/admin/notifications/test", {}),
    onSuccess: (r) => toast.success(`Test alert sent to ${r.recipients?.length || 0} recipient(s)`),
    onError: (e) => toast.error(apiError(e)),
  });
  const deskTestMut = useMutation({
    mutationFn: () => api.post("/admin/desk-sessions", { test: true, category: "Test Session" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["desk-sessions"] });
      toast.success("Test desk session created — check the Dashboard");
    },
    onError: (e) => toast.error(apiError(e)),
  });

  if (isLoading || !settings) {
    return <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  }

  const recipients = settings.recipients || [];
  const addRecipient = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    addMut.mutate({ email: email.trim().toLowerCase(), name: name.trim() });
  };

  return (
    <div className="space-y-5 animate-fade-up max-w-3xl" data-testid="notifications-page">
      <AppLinksCard />

      {/* Resend status */}
      {!settings.resend_ready && (
        <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="resend-warning">
          <AlertTriangle className="h-4.5 w-4.5 flex-shrink-0 mt-0.5" />
          <p>Email delivery isn't active yet. Add your <b>RESEND_API_KEY</b> in the <b>Secrets</b> tab, then restart the backend. Alerts will not be sent until this is configured.</p>
        </div>
      )}

      {/* Master toggle */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="master-toggle-card">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="grid place-items-center h-11 w-11 rounded-xl bg-indigo-50 text-indigo-600"><Bell className="h-5 w-5" /></div>
            <div>
              <h3 className="font-head text-base font-bold text-slate-900">Late Attendance Alerts</h3>
              <p className="text-sm text-slate-500 mt-0.5">Automatically email your broadcast list when an employee hasn't clocked in on time.</p>
            </div>
          </div>
          <button
            data-testid="alerts-enabled-toggle"
            aria-pressed={settings.enabled}
            disabled={updateMut.isPending}
            onClick={() => updateMut.mutate({ enabled: !settings.enabled })}
            className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full transition disabled:opacity-60 ${settings.enabled ? "bg-emerald-500" : "bg-slate-300"}`}
          >
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${settings.enabled ? "left-6" : "left-1"}`} />
          </button>
        </div>
      </div>

      {/* Threshold */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="threshold-card">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="h-4 w-4 text-slate-400" />
          <h3 className="font-head text-base font-bold text-slate-900">Alert Threshold</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">Trigger an alert this many minutes after an employee's shift start (plus their grace period), if they still haven't clocked in.</p>
        <div className="grid grid-cols-4 gap-2 max-w-md">
          {THRESHOLDS.map((t) => {
            const on = settings.threshold_minutes === t;
            return (
              <button key={t} data-testid={`threshold-${t}`} disabled={updateMut.isPending}
                onClick={() => updateMut.mutate({ threshold_minutes: t })}
                className={`h-12 rounded-lg text-sm font-bold border transition ${on ? "bg-indigo-600 border-indigo-600 text-white shadow-sm shadow-indigo-600/20" : "bg-white border-slate-200 text-slate-600 hover:border-indigo-300"}`}>
                {t} min
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-slate-400 flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" /> Shift times are interpreted in <b className="text-slate-500">{settings.timezone}</b>. Off-days are skipped automatically.
        </p>
      </div>

      {/* Recipients */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="recipients-card">
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="h-4 w-4 text-slate-400" />
            <h3 className="font-head text-base font-bold text-slate-900">Broadcast Recipients</h3>
          </div>
          <p className="text-sm text-slate-500">Everyone on this list receives an email for each late employee. Sender shows as <b>Attendance Alert</b>.</p>
        </div>

        <form onSubmit={addRecipient} className="p-5 flex flex-col sm:flex-row gap-2 border-b border-slate-100" data-testid="add-recipient-form">
          <input data-testid="recipient-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)"
            className="h-11 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 sm:w-44" />
          <input data-testid="recipient-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com"
            className="h-11 flex-1 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
          <button type="submit" disabled={addMut.isPending} data-testid="add-recipient-btn"
            className="h-11 px-4 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {addMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
          </button>
        </form>

        <div data-testid="recipients-list">
          {recipients.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-400">No recipients yet. Add an email above to start receiving alerts.</p>
          ) : (
            <ul className="divide-y divide-slate-50">
              {recipients.map((r) => (
                <li key={r.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50/60 transition" data-testid={`recipient-row-${r.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="grid place-items-center h-9 w-9 rounded-full bg-sky-50 text-sky-600 flex-shrink-0"><Mail className="h-4 w-4" /></div>
                    <div className="min-w-0">
                      {r.name && <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>}
                      <p className="text-sm text-slate-500 truncate">{r.email}</p>
                    </div>
                  </div>
                  <button onClick={() => delMut.mutate(r.id)} data-testid={`delete-recipient-${r.id}`}
                    className="h-8 w-8 grid place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition flex-shrink-0">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-5 border-t border-slate-100 flex justify-end">
          <button onClick={() => testMut.mutate()} disabled={testMut.isPending || recipients.length === 0}
            data-testid="send-test-btn"
            className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-2">
            {testMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Test Alert
          </button>
        </div>
      </div>

      {/* Desk session test */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid="desk-session-test-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="grid place-items-center h-11 w-11 rounded-xl bg-violet-50 text-violet-600"><Coffee className="h-5 w-5" /></div>
            <div>
              <h3 className="font-head text-base font-bold text-slate-900">Desk Session History</h3>
              <p className="text-sm text-slate-500 mt-0.5">Staff leave-desk / return-to-desk events (logged by the mobile app) appear on the Dashboard. Create a sample record to preview the table.</p>
            </div>
          </div>
          <button onClick={() => deskTestMut.mutate()} disabled={deskTestMut.isPending}
            data-testid="create-test-session-btn"
            className="h-10 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-60 inline-flex items-center gap-2">
            {deskTestMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create Test Session
          </button>
        </div>
      </div>
    </div>
  );
}
